import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FormDialog } from '../form/FormDialog';
import { FieldGroup } from '../form/FormField';
import { SelectField, TextareaField } from '../form/fields';
import type { SelectOption } from '../Select';

export type ReasonAction = 'hide' | 'archive';

export interface ReasonPayload {
  /**
   * Выбранная причина: текст (в аудите — «· причина: {reason}», SRV BR-SRV-62) или код, если
   * причины переданы вариантами `reasonOptions` (перечисление контракта, например `HideReason`).
   */
  reason: string;
  comment: string;
}

export interface ReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ReasonAction;
  recordTitle: string;
  /** Свой список причин; по умолчанию — списки из README «Опасные действия». */
  reasons?: readonly string[];
  /** Причины-коды контракта с подписями: в `onConfirm` уходит код (`value`), а не подпись. */
  reasonOptions?: readonly SelectOption[];
  onConfirm: (payload: ReasonPayload) => Promise<unknown>;
}

const COMMENT_MAX = 300;

/**
 * «Скрыть» и «Архивировать» (README «Опасные действия»): причина из списка + комментарий.
 */
export function ReasonDialog({
  open,
  onOpenChange,
  action,
  recordTitle,
  reasons,
  reasonOptions,
  onConfirm,
}: ReasonDialogProps) {
  const { t } = useTranslation(['dialogs', 'form']);
  const defaultReasons = useMemo(
    () =>
      action === 'hide'
        ? [
            t('hide.reasons.outOfStock'),
            t('hide.reasons.seasonClosed'),
            t('hide.reasons.contentEdits'),
            t('hide.reasons.partnerDecision'),
          ]
        : [
            t('archive.reasons.notRelevant'),
            t('archive.reasons.duplicate'),
            t('archive.reasons.contractTerminated'),
            t('archive.reasons.dataError'),
          ],
    [action, t],
  );
  const list = reasons ?? defaultReasons;
  const schema = useMemo(
    () =>
      z.object({
        reason: z.string().min(1, t('form:required')),
        comment: z.string().max(COMMENT_MAX),
      }),
    [t],
  );
  type Values = z.infer<typeof schema>;
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '', comment: '' },
  });

  useEffect(() => {
    if (open) form.reset({ reason: '', comment: '' });
  }, [open, form]);

  const isHide = action === 'hide';
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      onSubmit={(values) => onConfirm({ reason: values.reason, comment: values.comment.trim() })}
      overline={isHide ? t('hide.overline') : t('archive.overline')}
      title={isHide ? t('hide.title') : t('archive.title', { title: recordTitle })}
      lead={isHide ? t('hide.lead') : t('archive.lead')}
      note={isHide ? undefined : t('archive.note')}
      footNote={isHide ? t('hide.footNote') : t('archive.footNote')}
      submitLabel={isHide ? t('hide.submit') : t('archive.submit')}
      submitVariant={isHide ? 'solid' : 'danger'}
    >
      <FieldGroup>
        <SelectField<Values>
          name="reason"
          label={isHide ? t('hide.reason') : t('archive.reason')}
          required
          options={reasonOptions ?? list.map((reason) => ({ value: reason, label: reason }))}
        />
        <TextareaField<Values>
          name="comment"
          label={t('comment')}
          placeholder={isHide ? t('hide.commentPlaceholder') : t('archive.commentPlaceholder')}
          maxLength={COMMENT_MAX}
        />
      </FieldGroup>
    </FormDialog>
  );
}
